import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Music2, Sparkles, Shield, Zap, Check } from "lucide-react";
import DroneIcon from "../components/DroneIcon";
import PaymentBadges from "../components/PaymentBadges";
import { useAuth } from "../lib/auth";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/abddd589-7916-48de-9dda-5ca0303f4e2f/images/23859ccdf1053086f0cb272aefe8528eb4a0fb2ecaf160ee6324ff0ee7013cdb.png";
const LENS_IMG = "https://static.prod-images.emergentagent.com/jobs/abddd589-7916-48de-9dda-5ca0303f4e2f/images/5edbd165432f3cfcd041b8321ff6dd0b56838f679b339114f88dd9978a8c9387.png";
const AERIAL_1 = "https://images.unsplash.com/photo-1669003154697-56290eaf5023?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const AERIAL_2 = "https://images.unsplash.com/photo-1669003154364-6a183303f701?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const AERIAL_3 = "https://images.unsplash.com/photo-1641441371947-47dd2f4a4276?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

const PACKAGES = [
  { id: "single", name: "Tek Video", price: 350, credits: 1, highlight: false,
    features: ["1 adet 60 sn. video", "Logo filigranı", "Stok müzik seçimi", "Full HD indirme"] },
  { id: "pack5", name: "5'li Paket", price: 1500, credits: 5, highlight: true,
    features: ["5 video (video başı 300₺)", "Öncelikli render", "Tüm stok müzik kütüphanesi", "Özel pazarlama metni"] },
  { id: "pack10", name: "10'lu Paket", price: 2500, credits: 10, highlight: false,
    features: ["10 video (video başı 250₺)", "Kurumsal fatura", "Yüksek çözünürlük", "7/24 destek"] },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 relative overflow-hidden">
      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-slate-950/70 backdrop-blur-2xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#C29B62] to-[#7a5e34] flex items-center justify-center">
              <DroneIcon size={16} className="text-slate-950" />
            </div>
            <span className="font-display font-black text-lg tracking-tight">ProSanalDrone</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-300">
            <a href="#features" className="hover:text-white transition">Özellikler</a>
            <a href="#pricing" className="hover:text-white transition">Fiyatlar</a>
            <a href="#how" className="hover:text-white transition">Nasıl Çalışır</a>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <button data-testid="nav-dashboard-btn" onClick={() => navigate("/dashboard")}
                className="px-4 py-2 text-sm rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] transition">
                Panelim
              </button>
            ) : (
              <>
                <Link data-testid="nav-login-btn" to="/login" className="text-sm text-slate-300 hover:text-white">Giriş</Link>
                <Link data-testid="nav-register-btn" to="/register" className="px-4 py-2 text-sm rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] transition">
                  Başla
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 grid lg:grid-cols-12 gap-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#C29B62]/40 bg-[#C29B62]/10 text-[#D4AF7A] text-xs uppercase tracking-[0.25em] mb-8">
              <Sparkles size={12} /> AI destekli emlak pazarlama
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white">
              Arsanızı 60 saniyede<br />
              <span className="text-[#C29B62]">sinematik bir</span><br />
              drone filmine dönüştürün.
            </h1>
            <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Ada/parsel bilgisiyle, firma logonuzla ve stok müzikle; ana yola mesafe, denize mesafe ve imar bilgilerini
              pazarlamacı bir anlatımla sunan profesyonel sanal drone videoları üretin.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid="hero-cta-btn" onClick={() => navigate(user ? "/dashboard/create" : "/register")}
                className="group inline-flex items-center gap-2 px-6 py-4 rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] transition">
                Video oluşturmaya başla <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
              </button>
              <a href="#pricing" data-testid="hero-pricing-btn"
                className="px-6 py-4 rounded-md border border-white/15 hover:border-[#C29B62]/60 hover:bg-white/5 transition">
                Fiyatları gör
              </a>
            </div>
            <div className="mt-12 flex items-center gap-8 text-xs text-slate-400 uppercase tracking-[0.2em]">
              <div>60 sn sinematik</div>
              <div className="h-px flex-1 bg-white/10 max-w-20" />
              <div>Logo filigranı</div>
              <div className="h-px flex-1 bg-white/10 max-w-20" />
              <div>Stok müzik</div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }}
            className="lg:col-span-4 hidden lg:flex items-end">
            <div className="relative w-full aspect-[3/4] rounded-xl overflow-hidden border border-white/10">
              <img src={LENS_IMG} alt="drone lens" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C29B62]">Live demo</div>
                <div className="mt-2 font-display text-2xl">4K Aerial Experience</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-4">Öne Çıkan Özellikler</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
              Emlak portföyünüzü <span className="text-[#C29B62]">premium</span> bir seviyeye taşıyın.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <FeatureCard icon={<MapPin size={20} />} title="Ada & Parsel Sorgulama" body="parselsorgu.org.tr entegrasyonu ile otomatik konum verileri, imar ve yol/deniz mesafesi." className="md:col-span-2 lg:col-span-2" />
            <FeatureCard icon={<DroneIcon size={20} />} title="Sinematik Drone Efekti" body="Profesyonel drone kamera hareketleri ve renk tonlaması." />
            <FeatureCard icon={<Shield size={20} />} title="Logo Filigranı" body="Firma logonuz her karede, markanız her zaman önde." />
            <FeatureCard icon={<Music2 size={20} />} title="Stok Müzik Kütüphanesi" body="Sinematik, kurumsal, ambient tarzlarda telifsiz müzikler." />
            <FeatureCard icon={<Sparkles size={20} />} title="Pazarlama Anlatımı" body="Ana yol, deniz mesafesi, imar bilgisi ile ikna edici seslendirme metni." className="md:col-span-2 lg:col-span-2" />
            <FeatureCard icon={<Zap size={20} />} title="Hızlı Üretim" body="Ortalama 60 saniyede hazır, indirilebilir video." />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 md:py-32 border-y border-white/5 bg-slate-900/30 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-4">Süreç</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-10">4 adımda videonuz hazır.</h2>
            <ol className="space-y-6">
              {["İl / İlçe / Mahalle ve Ada / Parsel bilgilerini girin.",
                "Firma isminizi ve logonuzu yükleyin.",
                "Stok müzik kütüphanesinden fon müziğinizi seçin.",
                "Pazarlama detaylarını girin; 60 sn sinematik videonuzu indirin."].map((t, i) => (
                <li key={i} className="flex gap-5">
                  <div className="w-10 h-10 rounded-md bg-[#C29B62]/10 border border-[#C29B62]/30 text-[#C29B62] font-display font-bold flex items-center justify-center shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <p className="text-slate-300 pt-2 text-base">{t}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[AERIAL_1, AERIAL_2, AERIAL_3, LENS_IMG].map((src, i) => (
              <div key={i} className={`relative rounded-lg overflow-hidden border border-white/10 ${i === 0 || i === 3 ? 'row-span-1' : ''} aspect-[4/5]`}>
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 text-center">
            <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-4">Fiyatlandırma</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Her bütçeye uygun paket.</h2>
            <p className="text-slate-400 mt-4">KDV dahildir. Kredi kartı, havale ve kurumsal fatura desteklenir.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map(pkg => (
              <div key={pkg.id} data-testid={`pricing-card-${pkg.id}`}
                className={`relative rounded-xl p-8 bg-slate-900/40 backdrop-blur-xl transition hover:-translate-y-1 duration-300 ${pkg.highlight ? "tracing-border border-[#C29B62]/40" : "border border-white/10"}`}>
                {pkg.highlight && (
                  <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#C29B62] text-slate-950 text-[10px] uppercase tracking-[0.25em] font-bold">
                    Popüler
                  </div>
                )}
                <div className="relative z-10">
                  <div className="text-xs uppercase tracking-[0.25em] text-slate-400">{pkg.name}</div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-display text-5xl font-black">{pkg.price}</span>
                    <span className="text-xl text-slate-400">₺</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-2">{pkg.credits} video kredisi</div>
                  <ul className="mt-8 space-y-3">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex gap-3 items-start text-sm text-slate-200">
                        <Check size={16} className="text-[#C29B62] mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`pricing-cta-${pkg.id}`} onClick={() => navigate(user ? "/dashboard/packages" : "/register")}
                    className={`mt-8 w-full py-3 rounded-md font-semibold transition ${pkg.highlight ? "bg-[#C29B62] text-slate-950 hover:bg-[#D4AF7A]" : "border border-white/15 hover:border-[#C29B62]/60 hover:bg-white/5"}`}>
                    Satın Al
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid gap-10 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#C29B62] to-[#7a5e34] flex items-center justify-center">
                <DroneIcon size={14} className="text-slate-950" />
              </div>
              <span className="font-display font-bold">ProSanalDrone</span>
            </div>
            <p className="text-sm text-slate-500 max-w-xs">
              Türkiye'nin ilk sanal drone pazarlama platformu. Arsanızın sinematik drone videosunu 60 saniyede üretin.
            </p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-4">Yasal</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/iptal-iade" data-testid="footer-iptal-iade" className="text-slate-300 hover:text-[#C29B62]">İptal ve İade Koşulları</Link></li>
              <li><Link to="/teslimat" data-testid="footer-teslimat" className="text-slate-300 hover:text-[#C29B62]">Teslimat ve Hizmet Sunumu</Link></li>
              <li><Link to="/kvkk" data-testid="footer-kvkk" className="text-slate-300 hover:text-[#C29B62]">KVKK Aydınlatma Metni</Link></li>
              <li><Link to="/cerez-politikasi" data-testid="footer-cerez" className="text-slate-300 hover:text-[#C29B62]">Çerez Politikası</Link></li>
              <li><a href="mailto:destek@prosanaldrone.com" className="text-slate-300 hover:text-[#C29B62]">destek@prosanaldrone.com</a></li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-4">Güvenli Ödeme</div>
            <PaymentBadges data-testid="payment-badges" />
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              Tüm ödemeler <strong className="text-slate-300">iyzico</strong> güvencesiyle 256-bit SSL şifreleme üzerinden işlenir. Kart bilgileri sunucularımızda saklanmaz.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="text-slate-500 text-sm">© 2026 ProSanalDrone</span>
          <div className="text-xs text-slate-500 uppercase tracking-[0.25em]">
            Türkiye'nin ilk sanal drone pazarlama platformu
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, body, className = "" }) {
  return (
    <motion.div whileHover={{ y: -4 }}
      className={`p-8 rounded-xl bg-slate-900/40 backdrop-blur-xl border border-white/10 hover:border-[#C29B62]/40 transition ${className}`}>
      <div className="w-10 h-10 rounded-md bg-[#C29B62]/10 border border-[#C29B62]/30 text-[#C29B62] flex items-center justify-center mb-6">
        {icon}
      </div>
      <h3 className="font-display text-xl font-semibold mb-2">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{body}</p>
    </motion.div>
  );
}
